import React from 'react';

import { useCreateFile } from '@vlabs/api/point-api/client';
import validate from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import { useForm } from 'react-hook-form';

import st from './AddCardFileForm.module.sass';
import { useAddCardFileTranslation } from './i18n';

interface TCardFileFormValues {
  file: FileList;
  filename: string;
}

interface AddCardFileFormProps {
  onCancel?: VoidFunction;
  onSubmit: (values: TCardFileFormValues) => void;
}

export function AddCardFileForm({
  onSubmit,
  onCancel = undefined,
}: AddCardFileFormProps): React.JSX.Element {
  const { t } = useAddCardFileTranslation();
  const createFile = useCreateFile();

  const form = useForm<TCardFileFormValues>({
    defaultValues: {
      file: undefined,
      filename: '',
    },
  });

  form.watch((data, { name, type }) => {
    if (name === 'file' && type === 'change' && !!data.file) {
      form.setValue('filename', data.file?.item(0)?.name ?? '');
      form.trigger();
    }
  });

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <div className={st.Root}>
        <h5>{t('title')}</h5>

        <div className={st.FileInputContainer}>
          <Control.FileInput
            id="file"
            {...form.register('file', {
              required: validate.required(),
            })}
            errors={form.formState.errors}
          />
        </div>

        <div className={st.InputContainer}>
          <span>{t('name')}</span>
          <Control.Input
            id="filename"
            {...form.register('filename', {
              required: validate.required(),
            })}
            errors={form.formState.errors}
          />
        </div>

        <div className={st.ModalFooter}>
          <div className={st.ButtonWrapper}>
            <Control.Button
              className={st.Button}
              data-testid="submitAddFileButton"
              disabled={createFile.isPending}
              isLoading={createFile.isPending}
              type="submit"
            >
              {t('submit')}
            </Control.Button>
          </div>

          {!!onCancel && (
            <div className={st.ButtonWrapper}>
              <Control.Button
                className={st.Button}
                data-testid="closeAddFileButton"
                kind="secondary"
                onClick={onCancel}
                variant="outlined"
              >
                {t('cancel')}
              </Control.Button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}
